import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { HotToastService } from "@ngneat/hot-toast";
import { Observable, debounceTime, distinctUntilChanged, map, of } from "rxjs";
import { EditPage } from "../../../_shared/components/layout/pages/edit-page";
import { ButtonComponent } from "../../../_shared/form/components/button/button.component";
import { HorizontalTextInputComponent } from "../../../_shared/form/components/horizontal-text-input/horizontal-text-input.component";
import { HorizontalWrapperComponent } from "../../../_shared/form/components/horizontal-wrapper/horizontal-wrapper.component";
import { SelectComponent } from "../../../_shared/form/components/select/select.component";
import { ValidatorComponent } from "../../../_shared/form/components/validator/validator.component";
import { SelectOption } from "../../../_shared/models/option";
import { AddResponseMessage, UpdateResponseMessage } from "../../../_shared/models/responseMessage";
import {
  CreateRequiredStringPipe,
  IsFormRequiredPipe,
} from "../../../_shared/pipes/is-form-required.pipe";
import { GlobalVariables } from "../../../_shared/services/global.service";
import { CountryService } from "../../../person/services/country.service";
import { AddressFormModel } from "../../form-models/address-form-model";
import { Address, AddressList } from "../../models/address";
import { VwwLocation } from "../../models/location";
import { VatNumberResponse } from "../../models/vatNumberResponse";
import { AddressService } from "../../services/address.service";
import { LocationService } from "../../services/location.service";
import { formatAddressLabel } from "../../utils/address-utils";

export type AddressPart =
  | "Street"
  | "VatNumber"
  | "EnterpriseNumber"
  | "GlnNumber"
  | "Name"
  | "Location"
  | "Country"
  | "Email"
  | "StreetNrBus"
  | "Province"
  | "SendMail"
  | "Iban";

@Component({
  selector: "app-address-base-edit",
  templateUrl: "./address-base-edit.component.html",
  styleUrls: ["./address-base-edit.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    IsFormRequiredPipe,
    ReactiveFormsModule,
    ButtonComponent,
    SelectComponent,
    HorizontalTextInputComponent,
    CreateRequiredStringPipe,
    ValidatorComponent,
    HorizontalWrapperComponent,
  ],
})
export class AddressBaseEditComponent extends EditPage implements OnInit {
  @Input() form!: AddressFormModel;
  //TODO: cleanup based on IsReadonly
  // showForm and inlineEdit should be true and showSelect should be false when readonly is true
  @Input() showSelect: boolean = true;
  @Input() alignRight: boolean = true;
  @Input() isOrganisation: boolean = false;
  @Input() onlyAddress: boolean = false; //TODO: Dead code?
  @Input() fields: AddressPart[] | null = null; // Volgorde is van belang
  @Input() showForm: boolean = false;
  @Input() showButtons: boolean = true; //Rename or remove
  @Input() selectControl: FormControl = new FormControl();
  @Input() name = "Adres";
  @Input() isReadOnly: boolean = false;
  @Input() noCols: boolean = false;
  @Input() doVerifyAddress: boolean = true;

  @Input() isCompanyWithVat: boolean = false;
  @Input() showLabels = true;
  @Input() label: string = "Selecteer locatie";
  @Input() showNameOnEdit: boolean = false;

  @Input() organizationId?: number;
  @Input() inlineEdit: boolean = true;

  @Output() onUpdated = new EventEmitter<any>(); // functie na het bewaren van het adres
  @Output() onAdded = new EventEmitter<any>(); // functie na het bewaren van het adres
  @Output() onCancel = new EventEmitter<any>(); // functie na het bewaren van het adres
  addressOptionsObservable: Observable<SelectOption[]> = of([]);
  addressesLoaded: boolean = false;
  vatIsLoading: boolean = false;
  protected countriesObservable: Observable<SelectOption[]>;

  suggestions: string[] = [];
  suggestionsPost: VwwLocation[] = [];
  public addressNotFound: boolean = false;
  public addressSuggestion: AddressList | null = null;

  public forceShowName = false;

  //private visibilityCache = new Map<string, boolean>();

  @ViewChild("addressSelect") addressSelect!: SelectComponent;

  constructor(
    protected override modal: NgbModal,
    protected override toast: HotToastService,
    private addressService: AddressService,
    private locationService: LocationService,
    private countryService: CountryService,
    override route: ActivatedRoute,
    protected override cdr: ChangeDetectorRef
  ) {
    super(route, modal, toast, cdr);
    this.countriesObservable = this.countryService
      .getAllPublic()
      .pipe(
        map((list) =>
          list
            .map(
              (country) => ({ id: country.Nederlands, name: country.Nederlands }) as SelectOption
            )
            .sort((a, b) => a.name.localeCompare(b.name))
        )
      );
    this.loadAddresses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["fields"]) {
      this.cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    if (this.form) this.selectControl.setValue(this.form.Id.value, { emitEvent: false });
    else this.form = new AddressFormModel({} as AddressList);
    console.log(this.form);
    if (this.fields == null) {
      this.fields = [
        "Name",
        "StreetNrBus",
        "Location",
        "Province",
        "Country",
        "EnterpriseNumber",
        "VatNumber",
        "Iban",
        "Email",
      ];
    }
    if (!this.showSelect && !this.fields?.includes("Name")) this.form?.Name.disable();

    if (this.showSelect) {
      if (this.form.Id.value == 0 && this.fields?.includes("Name")) {
        //bij nieuw address name is required
        this.form.Name.addValidators(Validators.required);
      }
      if (this.fields?.includes("Street")) {
        this.form.Street.addValidators(Validators.required);
      }
      if (this.fields?.includes("StreetNrBus")) {
        this.form.Street.addValidators(Validators.required);
        this.form.Nr.addValidators(Validators.required);
      }
      if (this.fields?.includes("Location")) {
        this.form.PostCode.addValidators(Validators.required);
        this.form.Location.addValidators(Validators.required);
      }
    }
    if (!this.isReadOnly) this.verifyAddress(this.form);

    this.selectControl.valueChanges.subscribe((x) => {
      if (x) {
        this.addressService.getById(x).subscribe((address) => {
          this.form.patchValue(address, { emitEvent: false });
          this.form.markAllAsTouched();
          this.selectSuggestion(`${address.Street} ${address.PostCode} ${address.Location}`);
        });
        this.showForm = false;
        console.log("showForm false id", x);

        if (x != 0 && x != "0") {
          //delete new address from list
          this.addressOptionsObservable = this.addressOptionsObservable.pipe(
            map((options) => options.filter((x) => x.id != 0 && x.id != "0"))
          );
          //this.addressSelect.updateOptions(this.addressOptionsObservable);
        }
      } else {
        this.form.patchValue(new AddressFormModel(new AddressList()).value, {
          emitEvent: false,
        });
      }
    });

    if (!this.showSelect) {
      this.showForm = true;
    }

    this.form.Street.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((qry) => {
        if (qry && this.form.isInBelgium()) {
          var qryWithPostcode = qry + " " + (", " + this.form.PostCode.value || "");
          this.locationService.getLocationSuggestions(qryWithPostcode).subscribe((s) => {
            if (s && s.SuggestionResult.length > 0) {
              this.suggestions = s.SuggestionResult;
            } else {
              this.locationService.getLocationSuggestions(qry).subscribe((s) => {
                if (s && s.SuggestionResult.length > 0) {
                  this.suggestions = s.SuggestionResult;
                }
              });
            }
          });
        }
      });

    this.form.PostCode.valueChanges.subscribe((pc) => {
      if (this.form.isInBelgium() && pc && pc.length >= 4) {
        this.locationService
          .getLocationByPostcode(pc, GlobalVariables.organization?.Id)
          .subscribe((loc) => {
            if (loc) {
              this.form.Location.patchValue(loc.Gemeente, { emitEvent: false });
              this.form.Province.patchValue(loc.Province?.Name);
              this.form.Country.patchValue(loc.Country?.Name);
              if (this.form.Street.value) {
                let streetWithNr =
                  `${this.form.Street.value.trim()} ${this.form.Nr.value} ${this.form.Bus.value}`.trim();
                this.selectSuggestion(
                  `${streetWithNr}, ${this.form.PostCode.value} ${this.form.Location.value}`,
                  true
                );
              } else {
                this.selectSuggestion(
                  this.form.PostCode.value + " " + this.form.Location.value,
                  true
                );
              }
              this.verifyAddress(this.form);
            }
          });
      }
    });

    this.form.Location.valueChanges.subscribe((location) => {
      if (this.form.isInBelgium()) {
        if (location && location.length >= 3) {
          this.locationService.getByCity(location).subscribe((loc) => {
            if (loc) {
              this.suggestionsPost = loc;
              if (this.suggestionsPost.length == 1) {
                this.selectPost(this.suggestionsPost[0]);
              }
            }
          });
        }
      }
    });

    this.form.EnterpriseNumber.valueChanges.subscribe((enterpriseNumber) => {
      if (this.fields && this.fields.indexOf("EnterpriseNumber") > -1) {
        if (enterpriseNumber && enterpriseNumber.length > 3) {
          this.vatIsLoading = true;
          this.addressService.getKboData(enterpriseNumber).subscribe((kboData) => {
            this.vatIsLoading = false;
            if (kboData) {
              this.handleKboData(kboData);
            } else {
              this.alert(
                "Niet gevonden",
                "Het ondernemingsnummer '" +
                  enterpriseNumber +
                  "' werd niet gevonden in onze databank. Kijk het ondernemingsnummer nog eens goed na."
              );
            }
          });
        }
      }
    });

    this.form.VatNumber.valueChanges.subscribe((vat) => {
      if (this.fields && this.fields?.indexOf("VatNumber") > -1) {
        if (vat && vat.length > 3)
          this.addressService.search(vat).subscribe((addresses) => {
            if (
              false &&
              addresses &&
              addresses.length > 0 &&
              addresses[0].Id != this.selectControl.value
            ) {
              //var address = addresses[0];
              //this.confirm("Bestaat al", address.Name + " heeft hetzelfde BTW-nummer.<br>Wil je dit gebruiken?", "Ja, gebruik dit adres", "Neen, maak een nieuw adres aan.")?.then(x => {
              //  if (x) {
              //    this.addressSelectControl.patchValue(address.Id);
              //    this.form.patchValue(address);
              //  }
              //})
            } else {
              this.vatIsLoading = true;
              if (this.addressService.isBelgianVat(vat)) {
                this.addressService.getKboData(vat).subscribe((kboData) => {
                  this.vatIsLoading = false;
                  if (kboData) {
                    this.handleKboData(kboData);
                  } else {
                    this.alert(
                      "Niet gevonden",
                      "Het btw-nummer '" +
                        vat +
                        "' werd niet gevonden in onze databank. Kijk het btw-nummer nog eens goed na en denk eraan dat elk btw-nummer moet starten met de landcode, zoals bijv.\n'BE 0123.456.789'."
                    );
                  }
                });
              } else {
                this.addressService.getVatData(vat).subscribe((vatData) => {
                  this.vatIsLoading = false;
                  if (vatData && vatData.valid) {
                    this.handleVatData(vatData);
                  } else {
                    if (vatData?.valid == false)
                      this.alert(
                        "Niet gevonden",
                        "Het btw-nummer '" +
                          vat +
                          "' werd niet gevonden in onze databank. Kijk het btw-nummer nog eens goed na en denk eraan dat elk btw-nummer moet starten met de landcode, zoals bijv.\n'BE 0123.456.789'."
                      );
                    else
                      this.alert(
                        "Btw-nummer opzoeken tijdelijk niet mogelijk",
                        "Assist gebruikt een externe databank om btw-nummers op te zoeken en te controleren. Momenteel krijgt Assist geen antwoord van deze externe databank. Gelieve daarom de gegevens van deze organisatie manueel aan te vullen."
                      );
                  }
                });
              }
            }
          });
      }
    });

    //uitzonderlijke gevallen
    // if (!this.form.Name.value) {
    //   if (this.form.Address.Name.value == "" || this.form.Address.Country.value == null) {
    //     this.addressForm.forceShowName = true;
    //   }
    // }
    this.loadAddresses();
  }

  onStreetBlur() {
    this.clearSuggestions();
    this.addressSuggestion = null;
    setTimeout(() => {
      this.verifyAddress(this.form);
    }, 0);
  }

  showAddressAlert() {
    const street = this.form.Street.value || "";
    const nr = this.form.Nr?.value || "";
    const postCode = this.form.PostCode?.value || "";

    this.alert(
      "Niet gevonden",
      `Assist heeft het adres <strong>${street} ${nr} met postcode ${postCode}</strong> niet teruggevonden in het adresregister van Vlaanderen. <br>Kijk even na of de straat en postcode juist zijn.`
    );
  }

  verifyAddress(form: AddressFormModel) {
    if (
      this.form.isInBelgium() &&
      this.doVerifyAddress &&
      form.Street.value &&
      form.PostCode.value
    ) {
      this.addressService
        .verifyAddress(form.Street.value, form.Nr.value, form.Bus.value, form.PostCode.value)
        .subscribe((data) => {
          this.addressNotFound = data.MatchScore == 0;
          this.addressSuggestion = null;

          if (data.MatchScore != null && data.MatchScore > 0) {
            const isDifferent =
              form.Street.value !== data.Street || form.Nr.value !== data.Nr;
            if (isDifferent) {
              this.addressSuggestion = data;
            } else {
              this.applySuggestedAddress(data, form);
            }
          }
        });
    }
  }

  showAddressSuggestion() {
    if (!this.addressSuggestion) return;
    const data = this.addressSuggestion;
    this.confirm(
      "Juiste adres?",
      `Voor het ingegeven adres '${this.form.Street.value} ${this.form.Nr.value}' werd het volgende adres gevonden '${data.Street} ${data.Nr}'. Wil je dit vervangen?`,
      "Ja, vervang adres",
      "Neen, gebruik ingegeven adres"
    )?.then((userConfirmed) => {
      if (userConfirmed) {
        this.applySuggestedAddress(data, this.form);
        this.addressSuggestion = null;
      }
    });
  }

  applySuggestedAddress(data: any, form: AddressFormModel) {
    if (data.Street) {
      form.Street.patchValue(data.Street, { emitEvent: false });
    }
    if (data.Nr) {
      form.Nr.patchValue(data.Nr, { emitEvent: false });
    }
    if (data.Bus) {
      form.Bus.patchValue(data.Bus, { emitEvent: false });
    }
    if (data.ExternalId) {
      form.ExternalId.patchValue(data.ExternalId, { emitEvent: false });
    }
    form.MatchScore.patchValue(data.MatchScore, { emitEvent: false });
  }

  handleKboData(kboData: AddressList) {
    if (this.form.Street.value) {
      this.confirm(
        "Adres vervangen?",
        "Assist heeft op basis van dit btw-nummer onderstaande adresgegevens gevonden:<br><br>" +
          "<strong>" +
          kboData.Name +
          "<br>" +
          kboData.Street +
          " " +
          kboData.Nr +
          "<br>" +
          kboData.PostCode +
          " " +
          kboData.Location +
          "<br></strong><br>" +
          "Wil je de gegevens die nu in Assist staan overschrijven door deze nieuwe gegevens?",
        "Ja, overschrijf",
        "Nee"
      )?.then((x) => {
        if (x) this.fillInAddressFromKbo(kboData);
      });
    } else {
      this.fillInAddressFromKbo(kboData);
    }
  }

  handleVatData(vatData: VatNumberResponse) {
    if (this.form.Street.value) {
      this.confirm(
        "Adres vervangen?",
        "Assist heeft op basis van dit btw-nummer onderstaande adresgegevens gevonden:<br><br>" +
          "<strong>" +
          vatData.name +
          "<br>" +
          vatData.address?.street +
          " " +
          vatData.address?.number +
          "<br>" +
          vatData.address?.zip_code +
          " " +
          vatData.address?.city +
          "<br></strong><br>" +
          "Wil je de gegevens die nu in Assist staan overschrijven door deze nieuwe gegevens?",
        "Ja, overschrijf",
        "Nee"
      )?.then((x) => {
        if (x) this.fillInAddressFromVat(vatData);
      });
    } else {
      this.fillInAddressFromVat(vatData);
    }
  }

  fillInAddressFromKbo(kboData: AddressList) {
    if (this.form.Id.value == 0 || this.form.Id.value == null || this.form.Id.value == undefined) {
      this.form.Name.patchValue(kboData.Name ?? "", { emitEvent: false });
    }
    this.form.Street.patchValue(kboData.Street ?? "", { emitEvent: false });
    this.form.Nr.patchValue(kboData.Nr ?? "", { emitEvent: false });
    this.form.Bus.patchValue(kboData.Bus ?? "", { emitEvent: false });
    this.form.PostCode.patchValue(kboData.PostCode ?? "", { emitEvent: false });
    this.form.Location.patchValue(kboData.Location ?? "", { emitEvent: false });
    this.form.Country.patchValue(kboData.Country ?? "", { emitEvent: false });
  }

  fillInAddressFromVat(vatData: VatNumberResponse) {
    this.form.Name.patchValue(vatData.name ?? "", { emitEvent: false });
    this.form.Street.patchValue(vatData.address?.street + " " + vatData.address?.number, {
      emitEvent: false,
    });
    this.form.PostCode.patchValue(vatData.address?.zip_code, { emitEvent: false });
    this.form.Location.patchValue(vatData.address?.city, { emitEvent: false });
    this.form.Country.patchValue(vatData.address?.country, { emitEvent: false });
  }

  /**
   * @deprecated
   * Is er een andere manier om dit te doen?
   */
  loadAddresses() {
    //TODO: make return type the same for both calls
    this.addressOptionsObservable = (
      this.organizationId
        ? this.addressService.getAll(`/byOrganization/${this.organizationId}`)
        : this.addressService.getAllOdata("").pipe(map((x) => x.Items))
    ).pipe(
      map((list) =>
        list
          .filter((x) => {
            const adres = formatAddressLabel(x);
            return adres != null;
          })
          .map((x) => {
            const adres = formatAddressLabel(x);
            return {
              id: x.Id,
              name: adres ?? "Leeg adres",
              style: { "font-style": adres ? "normal" : "italic" },
            } as SelectOption;
          })
          .sort((a, b) => a.name.localeCompare(b.name))
      )
    );
  }

  selectSuggestion(s: string, onlyCoordinates: boolean = false) {
    debugger;
    this.locationService.getAddress(s).subscribe((a) => {
      if (a.LocationResult && a.LocationResult.length > 0) {
        var address = a.LocationResult[0];
        if (!onlyCoordinates) {
          this.form.Street.patchValue(address.Thoroughfarename, { emitEvent: false });
          if (address.Housenumber)
            this.form.Nr.patchValue(address.Housenumber, { emitEvent: false });
          this.form.PostCode.patchValue(address.Zipcode, { emitEvent: false });
          this.form.Location.patchValue(address.Municipality, {
            emitEvent: false,
          });
          this.form.Country.patchValue("België", { emitEvent: false });
        }
        this.form.Latitude.patchValue(address.Location?.Lat_WGS84);
        this.form.Longitude.patchValue(address.Location?.Lon_WGS84);
        this.suggestions = [];
      } else {
        this.locationService
          .getAddress(this.form.PostCode.value + " " + this.form.Location.value)
          .subscribe((a) => {
            if (a.LocationResult && a.LocationResult.length > 0) {
              var address = a.LocationResult[0];
              this.form.Latitude.patchValue(address.Location?.Lat_WGS84);
              this.form.Longitude.patchValue(address.Location?.Lon_WGS84);
            } else {
              this.form.Latitude.patchValue(null);
              this.form.Longitude.patchValue(null);
            }
          });
      }
    });
  }

  getMapsUrl() {
    if (this.form.Latitude && this.form.Latitude.value)
      return (
        "https://www.google.be/maps/search/" +
        this.form.Latitude.value +
        "," +
        this.form.Longitude.value
      );
    else return "";
  }

  /*isVisible(fieldName: AddressPart): boolean {
    if (this.visibilityCache.has(fieldName)) {
      return this.visibilityCache.get(fieldName)!;
    }

    let visible = true;
    if (this.fields != null) {
      visible = this.fields.includes(fieldName);
    }

    this.visibilityCache.set(fieldName, visible);

    const formControl = this.form.get(fieldName);
    if (formControl) {
      visible ? formControl.enable({ emitEvent: false, onlySelf: true }) : formControl.disable({ emitEvent: false, onlySelf: true });
    }

    return visible;
  }*/

  onAdd() {
    this.selectControl.patchValue(null);
    if (this.inlineEdit) {
      this.showForm = true;
      if (!this.form)
        setTimeout(() => {
          document.getElementById("AddressName")?.focus();
          this.form.Name.reset();
        }, 100);
    } else {
      this.openEditModal();
    }
  }

  selectPost(post: VwwLocation) {
    this.form.PostCode.patchValue(post.postCode, { emitEvent: false });
    this.form.Location.patchValue(post.name, { emitEvent: false });
    this.suggestionsPost = [];
  }

  onEdit() {
    //TODO: is this getById duplicate from the selectControl valueChanges subscription?
    this.addressService.getById(this.selectControl.value).subscribe((address) => {
      if (this.inlineEdit) {
        this.showForm = true;
        this.form.patchValue(address);
        this.forceShowName = this.form.Name.invalid;
        setTimeout(() => {
          document.getElementById("AddressName")?.focus();
        }, 100);
      } else {
        this.openEditModal(address);
      }
    });
  }

  saveAddress() {
    this.isSaving = true;
    if (this.validateForm(this.form)) {
      if (this.form.Id.value && this.form.Id.value > 0) {
        this.addressService
          .update(this.form.mapToModel() as Address)
          .subscribe((x: UpdateResponseMessage) => {
            this.isSaving = false;
            if (this.onUpdated && this.onUpdated.observers.length > 0)
              this.onUpdated.emit(this.form.Id.value);
            else {
              this.showForm = false;
              this.forceShowName = false;
              this.loadAddresses();
              this.selectControl.patchValue(this.form.Id.value);
            }
          });
      } else {
        this.addressService
          .add(this.form.mapToModel() as Address)
          .subscribe((x: AddResponseMessage) => {
            this.isSaving = false;
            if (this.onAdded && this.onAdded.observers.length > 0) this.onAdded.emit(x.id);
            else {
              this.showForm = false;
              this.loadAddresses();
              this.selectControl.patchValue(x.id);
            }
          });
      }
    } else {
      this.isSaving = false;
    }
  }

  cancelEdit() {
    console.log("cancel");
    if (this.onCancel && this.onCancel.observers.length > 0) this.onCancel.emit();
    else this.showForm = false;
  }
  clearSuggestions() {
    setTimeout(() => {
      this.suggestions = [];
    }, 250);
  }
  clearSuggestionsPost() {
    setTimeout(() => {
      this.suggestionsPost = [];
    }, 250);
  }

  async openEditModal(adres?: AddressList) {
    const { AddAddressModalComponent } = await import(
      "../add-address-modal/add-address-modal.component"
    );
    const modal = this.modal.open(AddAddressModalComponent, { size: "lg" });
    const component: InstanceType<typeof AddAddressModalComponent> = modal.componentInstance;
    this.form.setModel(adres ?? ({ OrganizationId: this.organizationId } as AddressList));
    component.addAddressForm = this.form;
    component.fields = this.fields ?? [
      "Name",
      "StreetNrBus",
      "Location",
      "Country",
      "Email",
      "VatNumber",
    ];
    modal.result.then((addressId) => {
      if (adres) {
        this.emitOnUpdate();
      } else {
        this.selectControl.patchValue(addressId);
        this.emitOnAdd(addressId);
        this.loadAddresses();
      }
    });
  }

  emitOnUpdate() {
    if (this.onUpdated && this.onUpdated.observers.length > 0)
      this.onUpdated.emit(this.form.Id.value);
  }

  emitOnAdd(personId: number) {
    if (this.onAdded && this.onAdded.observers.length > 0) this.onAdded.emit(personId);
  }
}
